import { useId, useState, type FormEvent } from "react";
import { Outlet, useNavigate } from "react-router";
import { errorMessage, reportError } from "../../shared/api/errors.ts";
import { DeviceOption } from "../../shared/devices/DeviceOption.tsx";
import { forgetActiveDevice, useActiveDevice, useDevices } from "../../shared/devices/devices.ts";
import { parseInviteLink } from "../../shared/invites/inviteLink.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import type { SessionUser } from "../../shared/session/session.ts";
import { Avatar } from "../../shared/ui/Avatar/Avatar.tsx";
import { BottomSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button, ButtonLink } from "../../shared/ui/Button/Button.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import styles from "./ProfilePage.module.css";
import { InstallApp } from "../../shared/install/InstallApp.tsx";
import { Statistics } from "./Statistics.tsx";

/**
 * Таб «Аккаунт» (11.01 / 11.02): аккаунт, связанные E-va, принятие invite и выход (11.03).
 * «Email подтверждён», Push, тема и смена email не выводятся. Layout: «Изменить» (11.05) — шторка поверх профиля.
 */
export function ProfilePage() {
  const { user } = useSession();
  if (!user) return null;
  return (
    <main className={styles.page}>
      <Account user={user} />
      <LinkedDevices />
      <AcceptInvite />
      <InstallApp />
      <Statistics />
      <Logout />
      <Outlet />
    </main>
  );
}

function Account({ user }: { user: SessionUser }) {
  const titleId = useId();
  const { data: devices } = useDevices();
  const name = user.displayName?.trim() || "Без имени";
  // У аккаунта из Telegram нет email; username Telegram backend не хранит.
  const signIn = user.email ?? (user.telegramId ? "Вход через Telegram" : null);
  return (
    <section className={`${styles.card} ${styles.account}`} aria-labelledby={titleId}>
      <Avatar userId={user.id} name={name} size={64} />
      <div className={styles.accountText}>
        <h1 id={titleId} className={styles.name}>
          {name}
        </h1>
        {signIn && <p className={styles.meta}>{signIn}</p>}
        {devices && <p className={styles.meta}>Связано E-va: {devices.length}</p>}
      </div>
      <ButtonLink to="/profile/edit" variant="secondary" className={styles.edit}>
        Изменить
      </ButtonLink>
    </section>
  );
}

/** Связанные E-va с online-статусом; тап делает E-va активной и открывает таб «E-va». */
function LinkedDevices() {
  const titleId = useId();
  const query = useDevices();
  const { active, setActive } = useActiveDevice();
  const navigate = useNavigate();
  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <h2 id={titleId} className={styles.cardTitle}>
        Связанные устройства
      </h2>
      {query.isError ? (
        <>
          <p className={styles.error} role="alert">
            {errorMessage(query.error)}
          </p>
          <Button variant="secondary" onClick={() => void query.refetch()}>
            Повторить
          </Button>
        </>
      ) : !query.data ? (
        <div className={styles.loading} role="status" aria-label="Загрузка E-va">
          <Spinner size={32} />
        </div>
      ) : query.data.length === 0 ? (
        <>
          <p className={styles.hint}>Пока нет ни одной E-va.</p>
          <ButtonLink to="/claim/scan" variant="secondary">
            Подключить E-va
          </ButtonLink>
        </>
      ) : (
        <ul className={styles.list}>
          {query.data.map((device) => (
            <li key={device.id}>
              <DeviceOption
                device={device}
                aria-current={device.id === active?.id || undefined}
                onClick={() => {
                  setActive(device.id);
                  void navigate("/eva");
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {query.data && query.data.length > 0 && (
        <ButtonLink to="/claim/scan" variant="secondary">
          Подключить ещё E-va
        </ButtonLink>
      )}
    </section>
  );
}

/** Invite, пришедший не ссылкой: вставленная ссылка или токен ведут на экран принятия. */
function AcceptInvite() {
  const titleId = useId();
  const navigate = useNavigate();
  const [link, setLink] = useState("");
  const [error, setError] = useState<string>();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!link.trim()) {
      setError("Вставьте ссылку из приглашения");
      return;
    }
    const token = parseInviteLink(link);
    if (!token) {
      setError("Это не ссылка-приглашение E-va");
      return;
    }
    void navigate(`/invite/${encodeURIComponent(token)}`);
  }

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <h2 id={titleId} className={styles.cardTitle}>
        Принять приглашение
      </h2>
      <form className={styles.form} onSubmit={submit} noValidate>
        <Input
          label="Ссылка-приглашение"
          name="invite"
          inputMode="url"
          autoComplete="off"
          placeholder="https://…/invite/…"
          value={link}
          onChange={(event) => {
            setLink(event.target.value);
            setError(undefined);
          }}
          error={error}
        />
        <Button type="submit" variant="secondary">
          Продолжить
        </Button>
      </form>
    </section>
  );
}

/** «Выйти из аккаунта» с подтверждением (11.03): после выхода — Start, данные приложения стираются. */
function Logout() {
  const { logout } = useSession();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);

  async function confirm() {
    setPending(true);
    // Выход завершает сессию даже без ответа сервера: кэш TanStack Query очищает SessionProvider,
    // а выбор активной E-va не должен достаться следующему, кто войдёт в этом браузере.
    forgetActiveDevice();
    await logout().catch(reportError);
  }

  return (
    <>
      <Button variant="destructive" onClick={() => setConfirming(true)} aria-haspopup="dialog">
        Выйти из аккаунта
      </Button>
      <BottomSheet open={confirming} onClose={() => setConfirming(false)} title="Выйти из аккаунта?">
        <p className={styles.hint}>Чтобы снова отправлять сообщения на E-va, нужно будет войти.</p>
        <div className={styles.actions}>
          <Button variant="destructive" loading={pending} onClick={() => void confirm()}>
            Выйти
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Отмена
          </Button>
        </div>
      </BottomSheet>
    </>
  );
}
